import React, { useState } from 'react';
import { Mui, MuiStyles, MuiIcons } from '@osu/react-ui';
import AuthButton from '../components/AuthButton';
import JobsIndicator from '../components/JobsIndicator';
import { AUTH_ROLES, NAVBAR_WRAPPER_ID } from '../../util/constants';
import { Link, useLocation } from 'react-router-dom';
import ApplicantDetailsPageAppBar from '../../Applicant/components/appBar';
import ProgramTypeSwitcher from './ProgramTypeSwitcher';
import DefaultLandingPreference from './DefaultLandingPreference';
import { useOptInNavBadge, OptInNavBadge } from './ScholarsOptInNotification';
import useRoleAuthentication from "../../Authentication/hooks/useRoleAuthentication";
import { PROGRAMS } from '../../util/constants';
import { deriveProgramTypeFromPathname } from '../../util/scholars/scholarsScope';

const createClasses = (theme) => {
    return {
        appBar: {
          top: '0',
          zIndex: theme.zIndex.appBar,
          "& a:not(:last-child)": {
            marginRight: "1rem",
          }
        },
        root: {
          flexGrow: 1
        },
        menuButton: {
          marginRight: theme.spacing(2),
        },
        title: {
          flexGrow: 1,
        },
      }
};

const LINKS = [
    { path: "/"                             , label: "Application Review"       , roles: ["READ-ACCESS", "STUDENT-EMPLOYEE", AUTH_ROLES.HONORS_PROGRAM_COORDINATOR, AUTH_ROLES.HONORS_SEASONAL_MANAGER] },
    { path: "/manage-applications"          , label: "Manage"                   , roles: ["HONORS-ADMIN", "PROGRAM-ADMIN", AUTH_ROLES.HONORS_SEASONAL_MANAGER] },
    { path: "/reports"                      , label: "Reports"                  , roles: ["HONORS-ADMIN", "PROGRAM-ADMIN", AUTH_ROLES.HONORS_SEASONAL_MANAGER] },
 // { path: "/exports"                      , label: "Exports"                  , roles: ["HONORS-ADMIN", "PROGRAM-ADMIN"] },
    { path: "/reviewers/active"             , label: "Reviewers"                , roles: ["HONORS-ADMIN"] },
    { path: "/configurations"               , label: "Configurations"           , roles: ["HONORS-ADMIN"] },
    { path: "/configurations/cycle"         , label: "Cycles"                   , roles: [AUTH_ROLES.ADMIN_ACCESS] },
    { path: "/search"                       , label: "Search"                   , roles: ["HONORS-ADMIN", AUTH_ROLES.HONORS_PROGRAM_COORDINATOR, AUTH_ROLES.HONORS_SEASONAL_MANAGER] }, // Adding this to STUDENT-EMPLOYEE would be redundant & look weird as '/' auto-redirects here
];

// Scholars-scope nav links (shown when program type = scholars). Role-gated per link.
// Scholars nav is gated by SCHOLARS-* roles ONLY. The generic ADMIN-ACCESS role is a Honors-side role
// (a Honors admin holds HONORS-ADMIN + ADMIN-ACCESS) and must not surface any Scholars link — otherwise
// a Scholars Program Manager who is also a Honors admin would see the Scholars admin nav (Manage,
// Reviewers, Configurations) and land on admin pages that then fail with "Requires SCHOLARS-ADMIN".
const SCHOLARS_LINKS = [
    { path: "/scholars/queue"               , label: "Queue"                    , roles: [AUTH_ROLES.SCHOLARS_ADMIN, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER, AUTH_ROLES.SCHOLARS_REVIEWER] },
    { path: "/scholars/cascade"             , label: "Manage"                   , roles: [AUTH_ROLES.SCHOLARS_ADMIN] },
    { path: "/scholars/reports"             , label: "Reports"                  , roles: [AUTH_ROLES.SCHOLARS_ADMIN, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER] },
    { path: "/scholars/reviewers"           , label: "Reviewers"                , roles: [AUTH_ROLES.SCHOLARS_ADMIN] },
    { path: "/scholars/opt-in"              , label: "Opt-In List"              , roles: [AUTH_ROLES.SCHOLARS_ADMIN, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER] },
    { path: "/scholars/configurations"      , label: "Configurations"           , roles: [AUTH_ROLES.SCHOLARS_ADMIN] },
    { path: "/scholars/configurations/cycle", label: "Cycles"                   , roles: [AUTH_ROLES.SCHOLARS_ADMIN] },
    { path: "/scholars/search"              , label: "Search"                   , roles: [AUTH_ROLES.SCHOLARS_ADMIN] },
];

export default function Navbar() {
  const { isHonorsAdmin, isScholarsAdmin, user } = useRoleAuthentication()
  const { pathname } = useLocation();
  const isScholarsScope = deriveProgramTypeFromPathname(pathname) === PROGRAMS.SCHOLARS.KEY;
  const theme = MuiStyles.useTheme();
  const classes = createClasses(theme);
  const links = isScholarsScope ? SCHOLARS_LINKS : LINKS;

  // Collapse the inline links into a hamburger menu below `lg`. With up to 8 scope links plus the
  // right-side controls (default-landing, program switcher, jobs, auth), the inline row runs out of
  // horizontal room and the labels overlap on smaller viewports — the menu keeps them readable.
  const isCompact = Mui.useMediaQuery(theme.breakpoints.down('lg'));

  // Only the links this user can actually see — shared by both the inline row and the collapsed menu
  // so role gating and the resolved set stay identical across layouts.
  const visibleLinks = links.filter(({ roles }) => roles.some(role => user?.applicationRoles?.includes(role)));

  // has587 — new-applicant count for the Opt-In List link, rendered as an iOS-style corner badge on the
  // link itself (replaces the former standalone app-bar bell). 0 => no badge.
  const { count: optInBadgeCount } = useOptInNavBadge();

  return (
    <Mui.AppBar id={NAVBAR_WRAPPER_ID} color="inherit" sx={classes.appBar} position="sticky">
        <Mui.Toolbar>
            { isCompact
                ? <NavMenu links={visibleLinks} optInBadgeCount={optInBadgeCount} />
                : visibleLinks.map((link) => renderNavLink(link, optInBadgeCount)) }

            <Mui.Typography sx={{ flexGrow: 1 }} /> {/* Fill the horizontal space */}

            <DefaultLandingPreference />
            <ProgramTypeSwitcher />
            {(isHonorsAdmin || isScholarsAdmin) && <JobsIndicator />}
            <AuthButton />
        </Mui.Toolbar>

        <ApplicantDetailsPageAppBar />
    </Mui.AppBar>
  );
}

// Clicking a Honors-scope nav link is a deliberate in-session navigation, NOT a fresh landing, so it
// must NOT re-apply a Scholars default-landing preference. The Honors "Application Review" link targets
// "/" (the Dashboard), whose landing guard would otherwise bounce a Scholars-preference dual user to
// /scholars/queue. Signal intent with programSwitch (same flag the Honors program toggle uses) so the
// guard is skipped — the toggle in the top right remains the only way to actually switch sides.
const linkState = (path) => (path === "/" ? { programSwitch: true } : undefined);

// Path of the Scholars Opt-In List link — the one that carries the new-applicant badge.
const OPT_IN_PATH = "/scholars/opt-in";

// Pure render helper (NO hooks) for the wide inline layout. `whiteSpace: 'nowrap'` keeps multi-word
// labels (e.g. "Cascade Reports", "Opt-In List") on a single line so they never wrap-and-collide.
// has587 — the Opt-In List link is wrapped in an iOS-style corner badge showing the new-applicant count.
function renderNavLink({ path, label }, optInBadgeCount = 0) {
    const navbutton = {
        whiteSpace: 'nowrap',
        ":focus-visible": { "outline": "2pt solid #0060df", "border-radius": "2px" },
    };
    const button = <Mui.Button component={Link} key={path} to={path} state={linkState(path)} sx={navbutton}>{label}</Mui.Button>;
    if (path === OPT_IN_PATH) {
        return <OptInNavBadge key={path} count={optInBadgeCount}>{button}</OptInNavBadge>;
    }
    return button;
}

// Collapsed navigation: a single hamburger button that opens a menu of the (already role-filtered)
// links. Router `Link` state is preserved so the "/" landing-guard intent flag still travels.
// has587 — the Opt-In List menu item carries the same new-applicant corner badge as the inline link.
function NavMenu({ links, optInBadgeCount = 0 }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
        <React.Fragment>
            <Mui.IconButton
                edge="start"
                color="inherit"
                aria-label="Open navigation menu"
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleOpen}
                sx={{ mr: 1, ":focus-visible": { outline: "2pt solid #0060df", borderRadius: "2px" } }}
            >
                <MuiIcons.Menu />
            </Mui.IconButton>

            <Mui.Menu anchorEl={anchorEl} open={open} onClose={handleClose} keepMounted>
                {links.map(({ path, label }) => {
                    const item = (
                        <Mui.MenuItem
                            key={path}
                            component={Link}
                            to={path}
                            state={linkState(path)}
                            onClick={handleClose}
                            sx={{ minWidth: '12rem' }}
                        >
                            {label}
                            {path === OPT_IN_PATH && optInBadgeCount > 0 && (
                                <Mui.Badge
                                    badgeContent={optInBadgeCount}
                                    color="error"
                                    sx={{ ml: 'auto', pl: 2, '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
                                    data-testid="scholars-opt-in-nav-badge"
                                />
                            )}
                        </Mui.MenuItem>
                    );
                    return item;
                })}
            </Mui.Menu>
        </React.Fragment>
    );
}